import { defineStore } from 'pinia'
import { computed, onScopeDispose, ref, watch } from 'vue'

import { BASICS_TOUR_ID, TOURS, tourById, type TourStep } from '@/components/tour/tours'
import type { CoachmarkVariant } from '@/components/tour/AppCoachmark/coachmark-types'
import type { AnchorPlacement } from '@/components/primitives/AppPopover/use-anchored-position'
import type { Subscription } from '@/lib/firebase/subscribe'
import { createRetainer } from '@/lib/live/retainer'
import { useAuthStore } from '@/stores/auth'
import {
  EMPTY_TOUR_STATE,
  setTourState as repoSetTourState,
  subscribeTourState as repoSubscribeTourState,
} from '@/repositories/tour'
import { useChatStore } from '@/stores/chat'
import { useDialogStore } from '@/stores/dialog'
import type { SetTourStateInput, TourState } from '@/types/tour'
import { isLoaded } from '@/types/load-state'

export const TOUR_TARGET_GRACE_MS = 1500

export interface ActiveTourStep {
  target: HTMLElement | null
  titleKey: string
  bodyKey: string
  placement?: AnchorPlacement
  variant: CoachmarkVariant
  index: number
  count: number
  isFirst: boolean
  isLast: boolean
}

export interface TourSummary {
  id: string
  labelKey: string
  route?: string
  completed: boolean
}

interface TourRequest {
  id: string
  force: boolean
}

const MIRROR_STORAGE_PREFIX = 'raldo:tours:'

/** Local mirror of the tour state, keyed by uid; the server callable can be unavailable. */
function readMirror(key: string): TourState | null {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const value = parsed as Partial<TourState>
    return {
      seen: Array.isArray(value.seen) ? value.seen.filter((id) => typeof id === 'string') : [],
      skipped: Array.isArray(value.skipped)
        ? value.skipped.filter((id) => typeof id === 'string')
        : [],
      tipsEnabled: typeof value.tipsEnabled === 'boolean' ? value.tipsEnabled : true,
      handoffAt: typeof value.handoffAt === 'string' ? value.handoffAt : undefined,
    }
  } catch {
    return null
  }
}

function writeMirror(key: string | null, state: TourState): void {
  if (key === null) return
  try {
    window.localStorage.setItem(key, JSON.stringify(state))
  } catch {
    // Best-effort: a blocked store costs a repeated tour.
  }
}

function mergeTourState(remote: TourState, local: TourState): TourState {
  return {
    seen: [...new Set([...remote.seen, ...local.seen])],
    skipped: [...new Set([...remote.skipped, ...local.skipped])],
    tipsEnabled: remote.tipsEnabled && local.tipsEnabled,
    handoffAt: remote.handoffAt ?? local.handoffAt,
  }
}

export interface StartTourOptions {
  force?: boolean
}

export const useTourStore = defineStore('tour', () => {
  const tourState = ref<TourState>(EMPTY_TOUR_STATE)
  const stateLoaded = ref(false)
  let mirrorKey: string | null = null

  let sub: Subscription<TourState> | null = null
  let stopStateWatch: (() => void) | null = null

  function openState(): void {
    const uid = useAuthStore().user?.uid
    if (!uid || sub) return
    mirrorKey = `${MIRROR_STORAGE_PREFIX}${uid}`
    const mirrored = readMirror(mirrorKey)
    if (mirrored) {
      tourState.value = mirrored
      stateLoaded.value = true
    }
    const next = repoSubscribeTourState(uid)
    sub = next
    stopStateWatch = watch(
      next.state,
      (state) => {
        if (!isLoaded(state)) return
        // The mirror may hold writes the server has not accepted; union, never replace.
        tourState.value = mergeTourState(state.value, tourState.value)
        stateLoaded.value = true
      },
      { immediate: true },
    )
  }

  function closeState(): void {
    sub?.stop()
    sub = null
    stopStateWatch?.()
    stopStateWatch = null
    tourState.value = EMPTY_TOUR_STATE
    stateLoaded.value = false
    mirrorKey = null
    reset()
    queue.value = []
  }

  const stateRetainer = createRetainer({ open: openState, close: closeState })
  onScopeDispose(stateRetainer.dispose)

  const targets = ref<ReadonlyMap<string, HTMLElement>>(new Map())

  function registerTarget(key: string, element: HTMLElement): void {
    const next = new Map(targets.value)
    next.set(key, element)
    targets.value = next
  }

  function unregisterTarget(key: string, element?: HTMLElement): void {
    const current = targets.value.get(key)
    if (current === undefined) return
    if (element !== undefined && current !== element) return
    const next = new Map(targets.value)
    next.delete(key)
    targets.value = next
  }

  function targetFor(key: string): HTMLElement | null {
    return targets.value.get(key) ?? null
  }

  const activeTourId = ref<string | null>(null)
  const activeSteps = ref<readonly TourStep[]>([])
  const stepIndex = ref(0)
  const queue = ref<readonly TourRequest[]>([])

  let resolving: string | null = null

  const seen = computed(() => new Set(tourState.value.seen))
  const skipped = computed(() => new Set(tourState.value.skipped))
  const tipsEnabled = computed(() => tourState.value.tipsEnabled)

  const sectionToursUnlocked = computed(
    () => seen.value.has(BASICS_TOUR_ID) || skipped.value.has(BASICS_TOUR_ID),
  )

  /** Whether the post-onboarding offer is still owed, read from stored state. */
  const canOfferHandoff = computed(
    () =>
      stateLoaded.value && tourState.value.handoffAt === undefined && !isCompleted(BASICS_TOUR_ID),
  )

  const dockOpen = ref(false)

  /** Mirrors the chat dock's open state into the store; `TourHost` syncs it. */
  function setDockOpen(open: boolean): void {
    dockOpen.value = open
  }

  /** Whether a tour may take over the screen now; `tipsEnabled` is checked in `start`, not here. */
  const canStart = computed(() => {
    if (useDialogStore().open) return false
    if (dockOpen.value) return false
    const status = useChatStore().sendStatus
    return status !== 'submitting' && status !== 'streaming'
  })

  const activeStep = computed<ActiveTourStep | null>(() => {
    const id = activeTourId.value
    if (id === null) return null
    const tour = tourById(id)
    const step = activeSteps.value[stepIndex.value]
    if (!tour || !step) return null
    return {
      target: targets.value.get(step.targetKey) ?? null,
      titleKey: step.titleKey,
      bodyKey: step.bodyKey,
      placement: step.placement,
      variant: tour.variant,
      index: stepIndex.value,
      count: activeSteps.value.length,
      isFirst: stepIndex.value === 0,
      isLast: stepIndex.value === activeSteps.value.length - 1,
    }
  })

  function reset(): void {
    activeTourId.value = null
    activeSteps.value = []
    stepIndex.value = 0
  }

  function record(patch: SetTourStateInput): void {
    tourState.value = {
      ...tourState.value,
      seen: patch.seen
        ? [...new Set([...tourState.value.seen, ...patch.seen])]
        : tourState.value.seen,
      skipped: patch.skipped
        ? [...new Set([...tourState.value.skipped, ...patch.skipped])]
        : tourState.value.skipped,
      tipsEnabled: patch.tipsEnabled ?? tourState.value.tipsEnabled,
      handoffAt: patch.stampHandoff
        ? (tourState.value.handoffAt ?? new Date().toISOString())
        : tourState.value.handoffAt,
    }
    writeMirror(mirrorKey, tourState.value)
    void repoSetTourState(patch).catch((cause: unknown) => {
      console.error('setTourState failed; tour progress is local to this device only', cause)
    })
  }

  function isCompleted(id: string): boolean {
    return seen.value.has(id) || skipped.value.has(id)
  }

  function markSeen(id: string): void {
    record({ seen: [id] })
  }

  const tours = computed<readonly TourSummary[]>(() =>
    Object.values(TOURS).map((tour) => ({
      id: tour.id,
      labelKey: tour.labelKey,
      route: tour.route,
      completed: isCompleted(tour.id),
    })),
  )

  const completedTourCount = computed(() => tours.value.filter((tour) => tour.completed).length)

  function enqueue(request: TourRequest): void {
    if (queue.value.some((entry) => entry.id === request.id)) return
    queue.value = [...queue.value, request]
  }

  function dequeue(): void {
    if (!canStart.value) return
    const [next, ...rest] = queue.value
    if (!next) return
    queue.value = rest
    void run(next)
  }

  function resolveSteps(steps: readonly TourStep[]): Promise<readonly TourStep[]> {
    const present = (): readonly TourStep[] =>
      steps.filter((step) => targets.value.has(step.targetKey))

    if (present().length === steps.length) return Promise.resolve(steps)

    return new Promise((resolve) => {
      const stop = watch(targets, () => {
        if (present().length !== steps.length) return
        clearTimeout(timer)
        stop()
        resolve(steps)
      })
      const timer = setTimeout(() => {
        stop()
        resolve(present())
      }, TOUR_TARGET_GRACE_MS)
    })
  }

  async function run(request: TourRequest): Promise<void> {
    const tour = tourById(request.id)
    if (!tour) return

    resolving = request.id
    const steps = await resolveSteps(tour.steps)
    if (resolving !== request.id) return
    resolving = null

    if (steps.length === 0) {
      dequeue()
      return
    }

    activeTourId.value = tour.id
    activeSteps.value = steps
    stepIndex.value = 0
  }

  function start(id: string, options: StartTourOptions = {}): void {
    const force = options.force ?? false
    if (!tourById(id)) return
    if (!force && (isCompleted(id) || !tipsEnabled.value)) return
    if (activeTourId.value === id || resolving === id) return

    const request: TourRequest = { id, force }
    if (activeTourId.value !== null || resolving !== null || !canStart.value) {
      enqueue(request)
      return
    }
    void run(request)
  }

  function end(patch: SetTourStateInput | null): void {
    if (patch) record(patch)
    reset()
    dequeue()
  }

  function finish(): void {
    const id = activeTourId.value
    end(id ? { seen: [id] } : null)
  }

  function skip(): void {
    const id = activeTourId.value
    end(id ? { skipped: [id] } : null)
  }

  function next(): void {
    if (activeTourId.value === null) return
    if (stepIndex.value >= activeSteps.value.length - 1) {
      finish()
      return
    }
    stepIndex.value += 1
  }

  function back(): void {
    if (activeTourId.value === null) return
    if (stepIndex.value === 0) return
    stepIndex.value -= 1
  }

  function setTipsEnabled(enabled: boolean): void {
    if (tourState.value.tipsEnabled === enabled) return
    if (!enabled) reset()
    record({ tipsEnabled: enabled })
  }

  function stampHandoff(): void {
    if (tourState.value.handoffAt !== undefined) return
    record({ stampHandoff: true })
  }

  function acceptHandoff(): void {
    stampHandoff()
    start(BASICS_TOUR_ID)
  }

  function declineHandoff(): void {
    stampHandoff()
  }

  watch(canStart, (ready) => {
    if (ready && activeTourId.value === null && resolving === null) dequeue()
  })

  return {
    tourState,
    stateLoaded,
    seen,
    skipped,
    tipsEnabled,
    canStart,
    sectionToursUnlocked,
    tours,
    completedTourCount,
    canOfferHandoff,
    activeTourId,
    stepIndex,
    activeStep,
    retain: stateRetainer.retain,
    registerTarget,
    unregisterTarget,
    targetFor,
    setDockOpen,
    start,
    next,
    back,
    skip,
    finish,
    markSeen,
    setTipsEnabled,
    acceptHandoff,
    declineHandoff,
  }
})
